import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { sharedStyles } from '../styles/sharedStyles';
import { formatDateTimeForDisplay, formatDateForDisplay } from '../utils/timezone';
import AddTicketForm from '../components/AddTicketForm';
import DuplicateTicketModal from '../components/DuplicateTicketModal';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import ViewEditTicketModal from '../components/ViewEditTicketModal';
import SearchableDropdown from '../components/SearchableDropdown';
import { useUser } from '../hooks/useUser';
import { hideFinancialUi } from '../utils/foremanUi';

const Tickets = () => {
  const api = useApi();
  const { user } = useUser();
  const hideMoney = hideFinancialUi(user?.role);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    type: '', // 'approve' or 'reject'
    ticketId: null,
    rejectionReason: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10
  });
  const [pageInput, setPageInput] = useState('');
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: 'Pending',
    workDate: '',
    customerId: '',
    jobId: ''
  });
  const [duplicateTicketData, setDuplicateTicketData] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editTicketMode, setEditTicketMode] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [ticketToDuplicate, setTicketToDuplicate] = useState(null);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState('');

  // Fetch tickets with pagination and filters
  const fetchTickets = async () => {
    setLoading(true);
    setError('');
    try {
      // Filter out empty values to avoid sending empty strings to the API
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
     
      const params = new URLSearchParams({
        page: currentPage,
        limit: pagination.limit,
        ...cleanedFilters
      });

      console.log('Fetching tickets with params:', params.toString());

      const response = await api({
          method: 'get',
        url: `/tickets?${params}`
      });
     
      console.log('Tickets API response:', response);
     
      if (response.data.success) {
        setTickets(response.data.data.tickets);
        setPagination(response.data.data.pagination);
      } else {
        setError(response.data.message || 'Error fetching tickets');
        }
      } catch (err) {
      console.error('Error fetching tickets:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        headers: err.response?.headers,
        config: {
          url: err.config?.url,
          method: err.config?.method,
          headers: err.config?.headers
        }
      });
      setError(err.response?.data?.message || 'Error fetching tickets. Please check your authentication and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api({ method: 'get', url: '/customers/dropdown' });
        setCustomers(res.data.data || []);
      } catch (err) {
        setCustomers([]);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch jobs when customer changes
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const params = filters.customerId ? { customerId: filters.customerId } : {};
        const res = await api({ method: 'get', url: '/jobs/dropdown', params });
        setJobs(res.data.data || []);
      } catch (err) {
        setJobs([]);
      }
    };
    fetchJobs();
  }, [filters.customerId]);

  // Fetch employees on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api({ method: 'get', url: '/employees', params: { limit: 1000 } });
        setEmployees(res.data.data.employees || []);
      } catch (err) {
        setEmployees([]);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch tickets when page, filters, or limit change
  useEffect(() => {
    fetchTickets();
  }, [currentPage, filters, pagination.limit]);

  const handleApproveTicket = async (ticketId) => {
    setModalConfig({
      type: 'approve',
      ticketId,
      rejectionReason: ''
    });
    setShowModal(true);
  };

  const handleRejectTicket = async (ticketId) => {
    setModalConfig({
      type: 'reject',
      ticketId,
      rejectionReason: ''
    });
    setShowModal(true);
  };

  const handleModalConfirm = async () => {
    const { type, ticketId, rejectionReason } = modalConfig;
   
    try {
      const response = await api({
        method: 'patch',
        url: `/tickets/${ticketId}/status`,
        data: {
          status: type === 'approve' ? 'Approved' : 'Rejected',
          rejectionReason: type === 'reject' ? rejectionReason : undefined,
          approvedBy: type === 'approve' ? 1 : undefined // TODO: Get actual user ID
        }
      });
      
      if (response.data.success) {
        setSuccess(`Ticket ${type === 'approve' ? 'approved' : 'rejected'} successfully`);
        setShowModal(false);
        fetchTickets(); // Refresh the list
      } else {
        setError(response.data.message || `Error ${type === 'approve' ? 'approving' : 'rejecting'} ticket`);
      }
    } catch (err) {
      console.error(`Error ${type === 'approve' ? 'approving' : 'rejecting'} ticket:`, err);
      setError(err.response?.data?.message || `Error ${type === 'approve' ? 'approving' : 'rejecting'} ticket`);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => {
      // If customer changes, reset jobId
      if (name === 'customerId') {
        return { ...prev, customerId: value, jobId: '' };
      }
      return { ...prev, [name]: value };
    });
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
      try {
        const response = await api({
        method: 'patch',
        url: `/tickets/${ticketId}/status`,
        data: { status: newStatus }
        });

        if (response.data.success) {
        setSuccess(`Ticket status updated to ${newStatus}`);
        fetchTickets();
      } else {
        setError(response.data.message || 'Error updating ticket status');
        }
      } catch (err) {
      console.error('Error updating ticket status:', err);
      setError(err.response?.data?.message || 'Error updating ticket status');
    }
  };

  const handleAddTicketSuccess = (message) => {
    setSuccess(message);
    setShowAddTicketModal(false);
    fetchTickets();
  };

  const handleAddTicketClose = () => {
    setShowAddTicketModal(false);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'bg-warning text-dark';
      case 'Approved':
        return 'bg-success text-white';
      case 'Invoiced':
        return 'bg-info text-white';
      case 'Rejected':
        return 'bg-danger text-white';
      case 'Paid':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDateTime = (dateTimeStr) => {
    return formatDateTimeForDisplay(dateTimeStr);
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'Pending':
        return 'Pending Approval';
      case 'Approved':
        return 'Approved';
      case 'Invoiced':
        return 'Invoiced';
      case 'Rejected':
        return 'Rejected';
      case 'Paid':
        return 'Paid';
      default:
        return status;
    }
  };

  const getStatusDescription = (status) => {
    switch (status) {
      case 'Pending':
        return 'Awaiting approval from management';
      case 'Approved':
        return 'Approved and ready for billing';
      case 'Invoiced':
        return 'Invoice has been generated and sent';
      case 'Rejected':
        return 'Rejected - requires revision';
      case 'Paid':
        return 'Payment received and processed';
      default:
        return '';
    }
  };

  const formatHours = (qty, rateType) => {
    if (rateType === 'daily') return 'daily';
    if (rateType === 'load') {
      const loads = parseFloat(qty || 0);
      return loads === 0 ? '0 loads' : `${loads.toFixed(0)} loads`;
    }
    const hours = parseFloat(qty || 0);
    return hours === 0 ? '0 hrs' : `${hours.toFixed(2)} hrs`;
  };

  const formatDate = (dateStr) => {
    return formatDateForDisplay(dateStr);
  };

  const handleDuplicateTicket = (ticket) => {
    setTicketToDuplicate(ticket);
    setShowDuplicateModal(true);
  };

  const handleDuplicateTicketSubmit = async (ticketId, formData) => {
    try {
      const response = await api({
        method: 'post',
        url: `/tickets/${ticketId}/duplicate`,
        data: formData
      });

      if (response.data.success) {
        setSuccess('Ticket duplicated successfully');
        fetchTickets();
        return Promise.resolve();
      } else {
        throw new Error(response.data.message || 'Error duplicating ticket');
      }
    } catch (err) {
      console.error('Error duplicating ticket:', err);
      throw new Error(err.response?.data?.message || 'Error duplicating ticket');
    }
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setTicketToDuplicate(null);
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket? This action cannot be undone.')) {
      try {
        const response = await api({
          method: 'delete',
          url: `/tickets/${ticketId}`
        });

        if (response.data.success) {
          setSuccess('Ticket deleted successfully');
          fetchTickets();
        } else {
          setError(response.data.message || 'Error deleting ticket');
        }
      } catch (err) {
        console.error('Error deleting ticket:', err);
        setError(err.response?.data?.message || 'Error deleting ticket');
      }
    }
  };

  const handleViewTicket = (ticket, edit = false) => {
    setSelectedTicket(ticket);
    setEditTicketMode(edit);
    setShowTicketModal(true);
  };

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedTicket(null);
  };

  const handleBulkUpdateClick = () => {
    if (!bulkUpdateStatus) {
      setError('Please select a status to update');
      return;
    }
    if (pagination.total === 0) {
      setError('No tickets to update');
      return;
    }
    setShowBulkUpdateModal(true);
  };

  const handleBulkUpdateConfirm = async () => {
    try {
      // Send filters instead of ticketIds to update ALL tickets matching the filters
      const response = await api({
        method: 'patch',
        url: '/tickets/bulk-status',
        data: {
          filters: filters, // Send current filter state
          status: bulkUpdateStatus
        }
      });

      if (response.data.success) {
        setSuccess(response.data.message || `Successfully updated ${response.data.data.updatedCount} ticket(s) to ${bulkUpdateStatus}`);
        setShowBulkUpdateModal(false);
        setBulkUpdateStatus('');
        setCurrentPage(1); // Reset to first page after update
        fetchTickets(); // Refresh the list
      } else {
        setError(response.data.message || 'Error updating tickets');
      }
    } catch (err) {
      console.error('Error bulk updating tickets:', err);
      setError(err.response?.data?.message || 'Error updating tickets');
    }
  };

  const handleBulkUpdateCancel = () => {
    setShowBulkUpdateModal(false);
    setBulkUpdateStatus('');
  };

  // Generate pagination page numbers with ellipsis
  const getPaginationPages = () => {
    const totalPages = pagination.totalPages;
    const current = currentPage;
    const pages = [];
    const maxVisible = 12; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always show first page
    pages.push(1);

    if (current <= 6) {
      // Near the beginning: show 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, ..., last
      for (let i = 2; i <= 10; i++) {
        pages.push(i);
      }
      pages.push('ellipsis');
      pages.push(totalPages);
    } else if (current >= totalPages - 5) {
      // Near the end: show 1, ..., last-9, last-8, last-7, last-6, last-5, last-4, last-3, last-2, last-1, last
      pages.push('ellipsis');
      for (let i = totalPages - 9; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // In the middle: show 1, ..., current-4, current-3, current-2, current-1, current, current+1, current+2, current+3, current+4, ..., last
      pages.push('ellipsis');
      for (let i = current - 4; i <= current + 4; i++) {
        pages.push(i);
      }
      pages.push('ellipsis');
      pages.push(totalPages);
    }

    return pages;
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInput(value);
    }
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const page = parseInt(pageInput);
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      setPageInput('');
      } else {
      setError(`Please enter a page number between 1 and ${pagination.totalPages}`);
      setPageInput('');
    }
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    setCurrentPage(1);
  };

  return (
    <div style={{ padding: '2rem' }} className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div className="flex-grow-1 min-w-0">
          <h1 style={{
            fontSize: '2rem',
            fontWeight: '600',
            color: sharedStyles.colors.text.primary,
            marginBottom: '0.5rem',
            wordBreak: 'break-word'
          }}>
            Tickets
          </h1>
          <p style={{
            color: sharedStyles.colors.text.secondary,
            margin: 0
          }}>
            Manage equipment rental tickets and approvals
          </p>
        </div>
          <button
          className="btn btn-primary"
          onClick={() => setShowAddTicketModal(true)}
          style={{
            backgroundColor: sharedStyles.colors.primary.main,
            borderColor: sharedStyles.colors.primary.main,
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontWeight: '500',
            minHeight: '44px',
            whiteSpace: 'nowrap'
          }}
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Create New Ticket</span>
          <span className="d-sm-none">New Ticket</span>
          </button>
        </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
      </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <SearchableDropdown
                label="Customer"
                name="customerId"
                value={filters.customerId}
                onChange={handleFilterChange}
                options={customers}
                placeholder="All Customers"
                emptyMessage="No customers found"
                displayKey="companyName"
                valueKey="id"
                searchKeys={['companyName', 'contactPerson']}
                maxHeight="250px"
              />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <SearchableDropdown
                label="Job Name"
                name="jobId"
                value={filters.jobId}
                onChange={handleFilterChange}
                options={jobs}
                placeholder="All Jobs"
                emptyMessage="No jobs found"
                displayKey="title"
                valueKey="id"
                searchKeys={['title']}
                showCustomer={true}
                disabled={!filters.customerId || jobs.length === 0}
                maxHeight="250px"
              />
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Invoiced">Invoiced</option>
                <option value="Rejected">Rejected</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label">Work Date</label>
              <input
                type="date"
                className="form-control"
                name="workDate"
                value={filters.workDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="col-12 col-md-8 col-lg-4">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by ticket number, customer, or job..."
              />
            </div>
            <div className="col-12 col-md-4 col-lg-2 d-flex align-items-end">
                <button
                className="btn btn-outline-secondary w-100"
                onClick={() => {
                  setFilters({ search: '', status: 'Pending', workDate: '', customerId: '', jobId: '' });
                  setCurrentPage(1);
                }}
                style={{ minHeight: '44px' }}
              >
                Clear
                </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Update Controls */}
      {user?.role !== 'foreman' && pagination.total > 0 && (
        <div className="card mb-4" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4">
                <label className="form-label fw-bold">Bulk Update Status</label>
                <select
                  className="form-select"
                  value={bulkUpdateStatus}
                  onChange={(e) => setBulkUpdateStatus(e.target.value)}
                >
                  <option value="">Select Status...</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                  <option value="Invoiced">Invoiced</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="col-12 col-md-4">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleBulkUpdateClick}
                  disabled={!bulkUpdateStatus || pagination.total === 0}
                  style={{
                    backgroundColor: sharedStyles.colors.primary.main,
                    borderColor: sharedStyles.colors.primary.main,
                    minHeight: '44px'
                  }}
                >
                  <i className="bi bi-check-all me-2"></i>
                  Update All {pagination.total} Ticket{pagination.total !== 1 ? 's' : ''}
                </button>
            </div>
              <div className="col-12 col-md-4">
                <p className="text-muted mb-0 small">
                  <i className="bi bi-info-circle me-1"></i>
                  This will update all {pagination.total} ticket{pagination.total !== 1 ? 's' : ''} matching your filters (across all pages)
                </p>
              </div>
            </div>
              </div>
        </div>
      )}

      {/* Tickets Table */}
      <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading tickets...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
              <h5 className="mt-3">No tickets found</h5>
              <p className="text-muted">Create your first ticket to get started</p>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddTicketModal(true)}
              >
                Create Ticket
              </button>
            </div>
          ) : (
          <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                <tr>
                    <th>Ticket #</th>
                  <th>Customer</th>
                  <th>Job Name</th>
                  <th>Work Date</th>
                    <th className="d-none d-lg-table-cell">Machines</th>
                  <th>Total Hours</th>
                  {!hideMoney && <th>Total Amount</th>}
                  <th>Status</th>
                    <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>
                        <strong>{ticket.ticketNumber}</strong>
                        <br />
                      <small className="text-muted">
                          {formatDate(ticket.workDate)}
                      </small>
                    </td>
                      <td>
                        <strong className="d-block d-lg-none">{ticket.Customer?.companyName}</strong>
                        <span className="d-none d-lg-block">
                          <strong>{ticket.Customer?.companyName}</strong>
                          <br />
                          <small className="text-muted">
                            {ticket.Customer?.contactPerson}
                          </small>
                        </span>
                      </td>
                      <td>
                        <strong className="d-block d-lg-none">{ticket.Job?.title}</strong>
                        <span className="d-none d-lg-block">
                          <strong>{ticket.Job?.title}</strong>
                          <br />
                          <small className="text-muted">
                            {ticket.Job?.status}
                          </small>
                        </span>
                      </td>
                     
                      <td>{formatDate(ticket.workDate)}</td>
                      <td className="d-none d-lg-table-cell">
                        <div>
                          {ticket.TicketItems?.map((item, index) => (
                            <div key={index} className="mb-1">
                              <small>
                                <strong>{item.Machine?.name}</strong> - {formatHours(item.rateType === 'load' ? item.loadsUsed : item.hoursUsed, item.rateType)}
                                {item.Employee && (
                                  <span className="text-muted"> (Op: {item.Employee.name})</span>
                                )}
                              </small>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td>
                        <strong>{
                          ticket.TicketItems && ticket.TicketItems.length > 0
                            ? (() => {
                                const hourlyItems = ticket.TicketItems.filter(i => i.rateType === 'hourly');
                                const loadItems = ticket.TicketItems.filter(i => i.rateType === 'load');
                                const dailyItems = ticket.TicketItems.filter(i => i.rateType === 'daily');

                                if (hourlyItems.length > 0) {
                                  const totalHours = hourlyItems.reduce((sum, i) => sum + parseFloat(i.hoursUsed || 0), 0);
                                  return formatHours(totalHours, 'hourly');
                                }
                                if (loadItems.length > 0) {
                                  const totalLoads = loadItems.reduce((sum, i) => sum + parseFloat(i.loadsUsed || 0), 0);
                                  return formatHours(totalLoads, 'load');
                                }
                                if (dailyItems.length > 0) return 'daily';
                                return 'N/A';
                              })()
                            : 'N/A'
                        }</strong>
                      </td>
                      {!hideMoney && (
                        <td>
                          <strong>{formatCurrency(ticket.totalAmount)}</strong>
                        </td>
                      )}
                      <td>
                        <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                            {getStatusDisplay(ticket.status)}
                          </span>
                        <br />
                        <small className="text-muted d-none d-lg-block">
                              {getStatusDescription(ticket.status)}
                            </small>
                      </td>
                      <td>
                        <div className="btn-group" role="group" style={{ flexWrap: 'wrap', gap: '0.25rem' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleViewTicket(ticket)}
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handleViewTicket(ticket, true)}
                            title="Edit Ticket"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>

                          {/* Status Action Buttons */}
                          <button
                            type="button"
                            className={`btn btn-sm ${ticket.status === 'Pending' ? 'btn-warning' : 'btn-outline-warning'}`}
                            onClick={() => handleStatusUpdate(ticket.id, 'Pending')}
                            title="Set to Pending"
                          >
                            <i className="bi bi-clock"></i>
                          </button>
                         
                          <button
                            type="button"
                            className={`btn btn-sm ${ticket.status === 'Approved' ? 'btn-success' : 'btn-outline-success'}`}
                            onClick={() => handleStatusUpdate(ticket.id, 'Approved')}
                            title="Set to Approved"
                          >
                            <i className="bi bi-check-lg"></i>
                          </button>
                         
                          <button
                            type="button"
                            className={`btn btn-sm ${ticket.status === 'Invoiced' ? 'btn-info' : 'btn-outline-info'}`}
                            onClick={() => handleStatusUpdate(ticket.id, 'Invoiced')}
                            title="Set to Invoiced"
                          >
                            i
                          </button>
                         
                          <button
                            type="button"
                            className={`btn btn-sm ${ticket.status === 'Paid' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => handleStatusUpdate(ticket.id, 'Paid')}
                            title="Set to Paid"
                          >
                            P
                          </button>
                         
                          <button
                            type="button"
                            className={`btn btn-sm ${ticket.status === 'Rejected' ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => handleStatusUpdate(ticket.id, 'Rejected')}
                            title="Set to Rejected"
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                         
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleDuplicateTicket(ticket)}
                            title="Duplicate"
                          >
                            <i className="bi bi-files"></i>
                          </button>
                         
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-info"
                            onClick={() => navigate(`/ticket-reports?ticketNumber=${ticket.ticketNumber}`)}
                            title="View Report"
                          >
                            <i className="bi bi-graph-up"></i>
                          </button>
                         
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteTicket(ticket.id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {/* Modern Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-4">
          <div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <div className="card-body">
              <div className="row align-items-center g-3">
                {/* Left side - Info and items per page */}
                <div className="col-12 col-md-4">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span className="text-muted small">
                      Showing <strong>{(currentPage - 1) * pagination.limit + 1}</strong> to{' '}
                      <strong>{Math.min(currentPage * pagination.limit, pagination.total)}</strong> of{' '}
                      <strong>{pagination.total}</strong> tickets
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <label className="small text-muted mb-0">Per page:</label>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: 'auto', minWidth: '70px' }}
                        value={pagination.limit}
                        onChange={handleLimitChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Center - Page navigation */}
                <div className="col-12 col-md-4">
                  <nav aria-label="Page navigation">
                    <ul className="pagination pagination-sm justify-content-center mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          aria-label="First page"
                        >
                          <i className="bi bi-chevron-double-left"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          aria-label="Previous page"
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>
                     
                      {getPaginationPages().map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <li key={`ellipsis-${index}`} className="page-item disabled">
                              <span className="page-link">...</span>
                            </li>
                          );
                        }
                        
                        return (
                          <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                              aria-label={`Go to page ${page}`}
                              aria-current={currentPage === page ? 'page' : undefined}
                              style={{
                                minWidth: '38px'
                              }}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      })}
                     
                      <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === pagination.totalPages}
                          aria-label="Next page"
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                      <li className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(pagination.totalPages)}
                          disabled={currentPage === pagination.totalPages}
                          aria-label="Last page"
                        >
                          <i className="bi bi-chevron-double-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>

                {/* Right side - Jump to page */}
                <div className="col-12 col-md-4">
                  <form onSubmit={handlePageInputSubmit} className="d-flex align-items-center gap-2 justify-content-md-end">
                    <label className="small text-muted mb-0">Go to:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      style={{ width: '70px' }}
                      value={pageInput}
                      onChange={handlePageInputChange}
                      placeholder="Page"
                      aria-label="Page number"
                    />
                    <button
                      type="submit"
                      className="btn btn-sm btn-outline-primary"
                      disabled={!pageInput}
                    >
                      Go
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approval/Rejection Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalConfig.type === 'approve' ? 'Approve Ticket' : 'Reject Ticket'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {modalConfig.type === 'reject' && (
                  <div className="mb-3">
                    <label className="form-label">Rejection Reason</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={modalConfig.rejectionReason}
                      onChange={(e) => setModalConfig(prev => ({
                        ...prev,
                        rejectionReason: e.target.value
                      }))}
                      placeholder="Please provide a reason for rejection..."
                    ></textarea>
                  </div>
                )}
                <p>
                  Are you sure you want to {modalConfig.type} this ticket?
                  {modalConfig.type === 'approve' && ' This will approve all machines and hours in the ticket.'}
                </p>
              </div>
              <div className="modal-footer">
                <div className="d-flex gap-2 w-100 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-secondary flex-fill"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`btn flex-fill ${modalConfig.type === 'approve' ? 'btn-success' : 'btn-danger'}`}
                    onClick={handleModalConfirm}
                  >
                    {modalConfig.type === 'approve' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Ticket Modal */}
      {showAddTicketModal && (
        <AddTicketForm
          onSuccess={handleAddTicketSuccess}
          onClose={handleAddTicketClose}
        />
      )}

      {/* Modal Backdrop */}
      {showModal && <div className="modal-backdrop fade show"></div>}

      {showTicketModal && selectedTicket && (
        <ViewEditTicketModal
          ticket={selectedTicket}
          onClose={handleCloseTicketModal}
          onUpdated={fetchTickets}
          edit={editTicketMode}
        />
      )}

      {/* Duplicate Ticket Modal */}
      <DuplicateTicketModal
        show={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        originalTicket={ticketToDuplicate}
        onDuplicate={handleDuplicateTicketSubmit}
        customers={customers}
        jobs={jobs}
        employees={employees}
      />

      {/* Bulk Update Confirmation Modal */}
      {showBulkUpdateModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Bulk Status Update</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleBulkUpdateCancel}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to update the status of <strong>{pagination.total}</strong> ticket{pagination.total !== 1 ? 's' : ''}?</p>
                <div className="alert alert-info">
                  <strong>Current Status:</strong> Various<br />
                  <strong>New Status:</strong> {bulkUpdateStatus}<br />
                  <strong>Tickets to Update:</strong> {pagination.total} (all tickets matching your filters)
                </div>
                <p className="text-muted small mb-0">
                  This action will update all tickets matching your current filters across all pages, not just the ones visible on this page.
                </p>
              </div>
              <div className="modal-footer">
                <div className="d-flex gap-2 w-100 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-secondary flex-fill"
                    onClick={handleBulkUpdateCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary flex-fill"
                    onClick={handleBulkUpdateConfirm}
                  >
                    Update {pagination.total} Ticket{pagination.total !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal Backdrop */}
      {showBulkUpdateModal && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default Tickets;